import { siteConfig } from '../config/site'
import type { Post } from '../domain/content'
import { buildHomePageJsonLd } from './seo-home-jsonld'
import type { CaseStudyLong } from '../domain/longCaseStudy'
import type { SeoContext, SeoMeta } from '../domain/seo'

const joinUrl = (pathname: string) => new URL(pathname, `${siteConfig.siteUrl}/`).toString()

const baseSiteUrl = siteConfig.siteUrl.replace(/\/+$/, '')

const defaultImage = joinUrl(siteConfig.ogImagePath)

const defaultOgImageFields = {
  imageAlt: siteConfig.ogImageAlt,
  imageWidth: siteConfig.ogImageWidth,
  imageHeight: siteConfig.ogImageHeight,
} as const

/** Stable JSON-LD @id fragments (full URLs). */
export const schemaOrgIds = {
  organization: `${baseSiteUrl}#organization`,
  website: `${baseSiteUrl}#website`,
  person: `${baseSiteUrl}#person`,
} as const

const caseStudyAuthorFragment = '#case-study-author'

const toIsoDate = (d: string | undefined, fallback: string) => {
  const raw = d?.trim() || fallback
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return `${raw}T12:00:00.000Z`
  }
  return raw
}

const normalizeName = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ')

const isDefaultSiteAuthor = (caseStudyAuthorName: string | undefined) => {
  const a = caseStudyAuthorName?.trim()
  if (!a) return true
  return normalizeName(a) === normalizeName(siteConfig.authorName)
}

/** Rough markdown → plain text word count for schema.org wordCount. */
export const countWordsFromMarkdown = (md: string): number => {
  const stripped = md
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1 ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1 ')
    .replace(/^#{1,6}\s+/gm, ' ')
    .replace(/[*_>#|~-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!stripped) return 0
  return stripped.split(/\s+/).length
}

const imageObject = (url: string) => ({
  '@type': 'ImageObject',
  url,
})

const buildGuestAuthorNode = (canonicalPageUrl: string, cs: CaseStudyLong) => {
  const name = cs.author?.name?.trim() ?? ''
  const node: Record<string, unknown> = {
    '@type': 'Person',
    '@id': `${canonicalPageUrl}${caseStudyAuthorFragment}`,
    name: name || siteConfig.authorName,
  }
  const role = cs.author?.role?.trim()
  if (role) {
    node.jobTitle = role
  }
  return node
}

const buildBreadcrumbListNode = (pathname: string, label: string) => ({
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: joinUrl('/'),
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: label,
      item: joinUrl(`${pathname.replace(/\/+$/, '')}/`),
    },
  ],
})

const buildFaqPageNode = (items: { question: string; answer: string }[]) => ({
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
})

const longCaseArticleSection = (cs: CaseStudyLong) =>
  cs.hero?.client?.trim() || cs.hero?.tags?.[0]?.trim() || 'Case study'

const postArticleSection = (post: Post) => {
  if (post.type === 'note') {
    return post.tags[0]?.trim() || 'Notes'
  }
  return post.hero?.client?.trim() || post.hero?.tags?.[0]?.trim() || 'Case study'
}

const buildArticleNode = (opts: {
  canonicalPageUrl: string
  headline: string
  description: string
  datePublished: string
  dateModified: string
  imageUrl: string
  authorId: string
  articleSection: string
  wordCount: number
}) => ({
  '@type': 'Article',
  headline: opts.headline,
  description: opts.description,
  datePublished: toIsoDate(opts.datePublished, opts.datePublished),
  dateModified: toIsoDate(opts.dateModified, opts.datePublished),
  url: opts.canonicalPageUrl,
  image: imageObject(opts.imageUrl),
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': opts.canonicalPageUrl,
  },
  author: { '@id': opts.authorId },
  publisher: { '@id': schemaOrgIds.organization },
  isPartOf: { '@id': schemaOrgIds.website },
  inLanguage: 'en',
  articleSection: opts.articleSection,
  wordCount: opts.wordCount,
})

const buildPageSeoMeta = ({ pathname, canonicalPathname, kind, post, caseStudyLong, designPage }: SeoContext): SeoMeta => {
  const canonical = joinUrl((canonicalPathname ?? pathname).trim() || pathname)

  if (kind === 'design' && designPage) {
    return {
      title: `${designPage.title} | ${siteConfig.name}`,
      description: designPage.description,
      canonical: joinUrl(designPage.path),
      robots: 'index,follow,max-image-preview:large',
      ogType: 'website',
      image: designPage.image ? joinUrl(designPage.image.src) : defaultImage,
      ...(designPage.image ? { imageAlt: designPage.image.alt, imageWidth: designPage.image.width, imageHeight: designPage.image.height } : defaultOgImageFields),
    }
  }

  if (kind === 'home') {
    return {
      title: `${siteConfig.authorName} — AI flows for personalized B2B sales`,
      shareTitle: siteConfig.ogShareTitle,
      description: siteConfig.description,
      canonical,
      robots: 'index,follow,max-image-preview:large',
      ogType: 'website',
      image: defaultImage,
      ...defaultOgImageFields,
    }
  }

  if (kind === 'work') {
    return {
      title: `Work — Design, AI & writing | ${siteConfig.name}`,
      description: 'Product design, interaction prototypes, AI workflows and essays by Gosha Knyazhev.',
      canonical,
      robots: 'index,follow,max-image-preview:large',
      ogType: 'website',
      image: defaultImage,
      ...defaultOgImageFields,
    }
  }

  if (kind === 'notes-list') {
    return {
      title: `Blog — AI, design & product | ${siteConfig.name}`,
      description:
        'Case studies and essays on AI workflows, product design, personalized web and B2B sales experiences.',
      canonical,
      robots: 'index,follow,max-image-preview:large',
      ogType: 'website',
      image: defaultImage,
      ...defaultOgImageFields,
    }
  }

  if (kind === 'case-studies-list') {
    return {
      title: `Case studies — UX, systems & outcomes | ${siteConfig.name}`,
      description:
        'Case studies across UX, UI, design systems, and measurable product outcomes.',
      canonical,
      robots: 'index,follow,max-image-preview:large',
      ogType: 'website',
      image: defaultImage,
      ...defaultOgImageFields,
    }
  }

  if (kind === 'case-study-long' && caseStudyLong) {
    const cs = caseStudyLong
    const description =
      cs.seo?.description?.trim() ||
      cs.seo_summary?.body?.trim() ||
      'Case study: design workflow and outcomes.'
    const title = `${cs.seo?.title?.trim() || cs.page.title} | ${siteConfig.name}`
    const image = cs.seo?.image || defaultImage
    const usesDefaultOg = image === defaultImage
    const pub = cs.page.published_at || cs.date
    const mod = cs.page.updated_at || cs.date
    return {
      title,
      description,
      canonical,
      robots: 'index,follow,max-image-preview:large',
      ogType: 'article',
      image,
      ...(usesDefaultOg ? defaultOgImageFields : { imageAlt: cs.page.title }),
      articlePublishedTime: toIsoDate(pub, cs.date),
      articleModifiedTime: toIsoDate(mod, cs.date),
    }
  }

  if (kind === 'private-follow-up') {
    return {
      title: `Private follow-up | ${siteConfig.name}`,
      description: 'A private follow-up page prepared after a sales conversation.',
      canonical,
      robots: 'noindex,nofollow',
      ogType: 'website',
      image: defaultImage,
      ...defaultOgImageFields,
    }
  }

  if (kind === 'private-company-info') {
    return {
      title: `Company details | ${siteConfig.name}`,
      description: 'Private company details page.',
      canonical,
      robots: 'noindex,nofollow',
      ogType: 'website',
      image: defaultImage,
      ...defaultOgImageFields,
    }
  }

  if (kind === 'post' && post) {
    const image = post.seo?.image || defaultImage
    const usesDefaultOg = image === defaultImage
    const headline = post.type === 'note' ? post.title : post.page.title
    const description =
      post.seo?.description ||
      (post.type === 'note' ? post.excerpt : (post.seo_summary?.body ?? ''))
    return {
      title: `${post.seo?.title || headline} | ${siteConfig.name}`,
      description,
      canonical,
      robots: 'index,follow,max-image-preview:large',
      ogType: 'article',
      image,
      ...(usesDefaultOg ? defaultOgImageFields : { imageAlt: headline }),
      articlePublishedTime: toIsoDate(post.date, post.date),
      articleModifiedTime: toIsoDate(post.date, post.date),
    }
  }

  return {
    title: `Not Found | ${siteConfig.name}`,
    description: 'The requested page could not be found.',
    canonical,
    robots: 'noindex,follow',
    ogType: 'website',
    image: defaultImage,
    ...defaultOgImageFields,
  }
}

/** Apply URL and override rules once for SSR and browser metadata. */
export const buildSeoMeta = (context: SeoContext): SeoMeta => {
  const meta = buildPageSeoMeta(context)
  const canonical = new URL(meta.canonical)
  canonical.search = ''
  canonical.hash = ''
  if (!/\.[a-z0-9]+$/i.test(canonical.pathname) || canonical.pathname.endsWith('/kulud.ai')) {
    canonical.pathname = `${canonical.pathname.replace(/\/+$/, '')}/`
  }
  return {
    ...meta,
    title: context.title ? `${context.title} | ${siteConfig.name}` : meta.title,
    description: context.description ?? meta.description,
    canonical: canonical.toString(),
    image: joinUrl(context.ogImagePath || meta.image),
    imageAlt: context.ogImageAlt ?? meta.imageAlt,
    imageWidth: context.ogImageWidth ?? (context.ogImagePath ? undefined : meta.imageWidth),
    imageHeight: context.ogImageHeight ?? (context.ogImagePath ? undefined : meta.imageHeight),
  }
}

const buildHomeJsonLdFromSiteConfig = () =>
  buildHomePageJsonLd({
    siteUrl: siteConfig.siteUrl,
    siteName: siteConfig.name,
    description: siteConfig.description,
    authorName: siteConfig.authorName,
    authorTitle: siteConfig.authorTitle,
    linkedInUrl: siteConfig.linkedInUrl,
    organizationLogoUrl: siteConfig.organizationLogoUrl,
  })

export const buildJsonLd = ({
  pathname,
  canonicalPathname,
  kind,
  post,
  caseStudyLong,
  designPage,
  language = 'en',
}: SeoContext): Record<string, unknown> | null => {
  if (kind === 'private-follow-up' || kind === 'private-company-info' || kind === 'not-found') {
    return null
  }

  if (kind === 'home') {
    return buildHomeJsonLdFromSiteConfig()
  }

  const graph = buildHomeJsonLdFromSiteConfig()['@graph'] as Record<string, unknown>[]
  const seoPath = (canonicalPathname ?? pathname).trim() || pathname
  const canonicalPageUrl = buildSeoMeta({ pathname, canonicalPathname, kind, post, caseStudyLong, designPage }).canonical

  if (kind === 'work') {
    graph.push({ '@type': 'CollectionPage', '@id': canonicalPageUrl, url: canonicalPageUrl, name: 'Work', isPartOf: { '@id': schemaOrgIds.website }, inLanguage: language })
    graph.push(buildBreadcrumbListNode(pathname, 'Work'))
  }

  if (kind === 'design' && designPage) {
    const meta = buildSeoMeta({ pathname, kind, designPage })
    graph.push({
      '@type': designPage.id === 'home' || designPage.id === 'sides' ? 'CollectionPage' : 'WebPage',
      '@id': meta.canonical, url: meta.canonical, name: meta.title, description: meta.description,
      isPartOf: { '@id': schemaOrgIds.website }, author: { '@id': schemaOrgIds.person },
      primaryImageOfPage: imageObject(meta.image), inLanguage: language,
    })
    graph.push(buildBreadcrumbListNode(designPage.path, designPage.title))
  }

  const pushArticlePage = (opts: {
    headline: string
    description: string
    datePublished: string
    dateModified: string
    imageUrl: string
    authorId: string
    articleSection: string
    content: string
  }) => {
    graph.push(
      buildArticleNode({
        canonicalPageUrl,
        headline: opts.headline,
        description: opts.description,
        datePublished: opts.datePublished,
        dateModified: opts.dateModified,
        imageUrl: opts.imageUrl,
        authorId: opts.authorId,
        articleSection: opts.articleSection,
        wordCount: countWordsFromMarkdown(opts.content),
      }),
    )
  }

  if (kind === 'notes-list') {
    graph.push(buildBreadcrumbListNode(pathname, 'Blog'))
  }

  if (kind === 'case-studies-list') {
    graph.push(buildBreadcrumbListNode(pathname, 'Case Studies'))
  }

  if (kind === 'case-study-long' && caseStudyLong) {
    const cs = caseStudyLong
    const description =
      cs.seo?.description?.trim() ||
      cs.seo_summary?.body?.trim() ||
      'Case study: design workflow and outcomes.'
    const imageUrl = joinUrl(cs.seo?.image || defaultImage)
    const pub = cs.page.published_at || cs.date
    const mod = cs.page.updated_at || cs.date
    const defaultAuthor = isDefaultSiteAuthor(cs.author?.name)
    const authorId = defaultAuthor ? schemaOrgIds.person : `${canonicalPageUrl}${caseStudyAuthorFragment}`

    if (!defaultAuthor) graph.push(buildGuestAuthorNode(canonicalPageUrl, cs))

    pushArticlePage({
      headline: cs.page.title,
      description,
      datePublished: pub,
      dateModified: mod,
      imageUrl,
      authorId,
      articleSection: longCaseArticleSection(cs),
      content: cs.content,
    })

    graph.push(buildBreadcrumbListNode(seoPath, cs.page.title))

    const faqItems = cs.faq?.items
    if (faqItems?.length) {
      graph.push(buildFaqPageNode(faqItems))
    }
  }

  if (kind === 'post' && post) {
    const headline =
      post.type === 'note'
        ? post.title
        : post.page.title
    const description =
      post.type === 'note'
        ? post.excerpt
        : (post.seo_summary?.body ?? post.seo?.description ?? '')
    const imageUrl = joinUrl(post.seo?.image || defaultImage)

    pushArticlePage({
      headline,
      description,
      datePublished: post.date,
      dateModified: post.date,
      imageUrl,
      authorId: schemaOrgIds.person,
      articleSection: postArticleSection(post),
      content: post.content,
    })

    const crumbLabel = post.type === 'note' ? post.title : post.page.title
    graph.push(buildBreadcrumbListNode(seoPath, crumbLabel))
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  }
}
